import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import usePageMeta from '../usePageMeta';
import useJsonLd from '../useJsonLd';
import { contactLink } from '../topics';
import { useSiteContent, useContact } from '../SiteContent';
import { activeSite } from '../sites';
import { EINSATZGEBIET_24H, ORTE, ortLink } from '../orte';
import ContactSection from '../components/ContactSection';
import T from '../components/EditableText';
import PageHero from '../components/PageHero';
import FaqSection from '../components/FaqSection';
import CtaBand from '../components/CtaBand';
import FactBox from '../components/FactBox';

/** Erzeugt die Schlüssel `<präfix>.punkt-1` … `.punkt-<anzahl>`. */
const points = (prefix: string, count: number): string[] =>
  Array.from({ length: count }, (_, index) => `${prefix}.punkt-${index + 1}`);

const GRUNDPFLEGE_NUMBERS = [1, 2, 3];

/**
 * Die Fragen speisen den sichtbaren Abschnitt und das FAQ-Markup für Google —
 * eine Quelle, denn strukturierte Daten ohne sichtbaren Inhalt verstoßen gegen
 * Googles Richtlinien. Antworten mit Preisen dürfen nie von der Kostenseite abweichen.
 */
const FAQ_NUMBERS = [1, 2, 3, 4, 5, 6, 7];

const Betreuung24h: React.FC = () => {
  const contact = useContact();
  const { plain } = useSiteContent();

  // Titel und Beschreibung stehen bewusst im Code: Sie sind auf die Suchbegriffe
  // aus der Search Console geschnitten („24 stunden pflege in gifhorn" stand auf
  // Position 2,6 ohne einen Klick) und gehören nicht zu dem, was nebenbei
  // umformuliert wird.
  usePageMeta(
    '24-Stunden-Betreuung zu Hause in Gifhorn | Antczak',
    '24-Stunden-Pflege zu Hause in Gifhorn und Wolfsburg: Betreuungskraft wohnt im Haushalt — Grundpflege, Demenzbegleitung, Haushalt. Ab 100 € am Tag.',
    { standaloneTitle: true, image: '/website/img/24h-hero.jpg', preloadImage: '/website/img/24h-hero.webp' },
  );

  /**
   * `Service` sagt Google, was hier angeboten wird und wo — die Stammdaten des
   * Betriebs (`LocalBusiness`) stehen in `public/index.html` und werden über
   * `@id` verknüpft. Das FAQ-Markup macht aus den Fragen unten aufklappbare
   * Zeilen im Suchergebnis.
   */
  const jsonLd = React.useMemo(() => {
    const base = activeSite().url;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${base}/24h-betreuung#service`,
          name: '24-Stunden-Betreuung zu Hause',
          serviceType: '24-Stunden-Betreuung zu Hause',
          description: plain('betreuung24h.hero.absatz-2'),
          provider: { '@id': `${base}/#organisation` },
          areaServed: EINSATZGEBIET_24H.map((name) => ({ '@type': 'City', name })),
          url: `${base}/24h-betreuung`,
          offers: {
            '@type': 'Offer',
            priceCurrency: 'EUR',
            price: '100',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '100', priceCurrency: 'EUR', unitText: 'Tag', minPrice: '100' },
            url: `${base}/kosten`,
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQ_NUMBERS.map((number) => ({
            '@type': 'Question',
            name: plain(`betreuung24h.faq.${number}.frage`),
            acceptedAnswer: { '@type': 'Answer', text: plain(`betreuung24h.faq.${number}.antwort`) },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Startseite', item: `${base}/` },
            { '@type': 'ListItem', position: 2, name: '24-Stunden-Betreuung', item: `${base}/24h-betreuung` },
          ],
        },
      ],
    };
  }, [plain]);

  useJsonLd('betreuung24h', jsonLd);

  return (
    <>
      {/* Die H1 trägt bewusst den Suchbegriff samt Ort: die Seite stand
          für „24 stunden pflege in gifhorn" auf Position 2,6 und bekam
          trotzdem keinen einzigen Klick. */}
      <PageHero
        eyebrowKey="betreuung24h.hero.eyebrow"
        titleKey="betreuung24h.hero.titel"
        accentKey="betreuung24h.hero.titel-akzent"
        image={{ src: '/website/img/24h-hero.jpg', alt: 'Betreuerin hält die Hand einer Seniorin', width: 900, height: 1000, position: 'center 35%' }}
      >
        <p className="hx-hero__lead">
          <T k="betreuung24h.hero.absatz-1" />
        </p>
        <div className="btn-row hx-hero__actions">
          <Link to={contactLink('CARE_24H')} className="btn btn--primary">
            <T k="betreuung24h.hero.cta-beratung" />
          </Link>
          <a href={contact.phoneHref} className="btn btn--on-dark" data-stelle="hero">
            <Icon name="phone" size={18} />
            {contact.phoneLabel}
          </a>
          <a href={contact.whatsappHref} className="btn btn--on-dark" target="_blank" rel="noopener noreferrer">
            <Icon name="message-circle" size={18} />
            <T k="betreuung24h.hero.cta-whatsapp" />
          </a>
        </div>
      </PageHero>

      {/* Die Antwort in sechs Zeilen, bevor die Seite ins Detail geht. */}
      <section className="shell hx-facts-wrap">
        <FactBox prefix="betreuung24h.fakten" />
      </section>

      {/* Darauf können Sie bauen */}
      <section className="shell section hx-intro hx-intro--even">
        <div>
          <h2 className="h-section">
            <T k="betreuung24h.bauen.titel" />
          </h2>
          <div className="prose">
            <p>
              <T k="betreuung24h.hero.absatz-2" />
            </p>
            <p>
              <T k="betreuung24h.bauen.einleitung" />
            </p>
          </div>
        </div>
        <ul className="hx-checks hx-checks--lines">
          {points('betreuung24h.bauen', 9).map((k) => (
            <li key={k}><Icon name="check" size={17} /><span><T k={k} /></span></li>
          ))}
        </ul>
      </section>

      <CtaBand titleKey="betreuung24h.banner.titel" textKey="betreuung24h.banner.text" />

      <div className="hx-features">
      <section className="shell hx-feature">
        <div className="hx-feature__media">
          <img src="/website/img/24h-grundpflege.jpg" alt="Betreuerin hilft einer Seniorin beim Aufstehen" width={760} height={620} loading="lazy" />
        </div>
        <div className="hx-feature__body">
          <span className="hx-feature__num">01</span>
          <h2 className="h-block">
            <T k="betreuung24h.grundpflege.titel" />
          </h2>
          <p className="hx-feature__lead">
            <T k="betreuung24h.grundpflege.einleitung" />
          </p>
          <dl className="hx-feature__items">
            {GRUNDPFLEGE_NUMBERS.map((number) => (
              <div key={number}>
                <dt><T k={`betreuung24h.grundpflege.${number}.titel`} /></dt>
                <dd><T k={`betreuung24h.grundpflege.${number}.text`} /></dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="shell hx-feature hx-feature--rev">
        <div className="hx-feature__media">
          <img src="/website/img/24h-demenz.jpg" alt="Nachdenkliche Seniorin" width={760} height={620} loading="lazy" />
        </div>
        <div className="hx-feature__body">
          <span className="hx-feature__num">02</span>
          <h2 className="h-block">
            <T k="betreuung24h.demenz.titel" />
          </h2>
          <p className="hx-feature__lead">
            <T k="betreuung24h.demenz.einleitung" />
          </p>
          <ul className="hx-checks">
            {points('betreuung24h.demenz', 4).map((k) => (
              <li key={k}><Icon name="check" size={17} /><span><T k={k} /></span></li>
            ))}
          </ul>
        </div>
      </section>

      <section className="shell hx-feature">
        <div className="hx-feature__media">
          <img src="/website/img/24h-hauswirtschaft.jpg" alt="Betreuungskraft reinigt die Küche" width={760} height={620} loading="lazy" />
        </div>
        <div className="hx-feature__body">
          <span className="hx-feature__num">03</span>
          <h2 className="h-block">
            <T k="betreuung24h.hauswirtschaft.titel" />
          </h2>
          <p className="hx-feature__lead">
            <T k="betreuung24h.hauswirtschaft.einleitung" />
          </p>
          <ul className="hx-checks">
            {points('betreuung24h.hauswirtschaft', 6).map((k) => (
              <li key={k}><Icon name="check" size={17} /><span><T k={k} /></span></li>
            ))}
          </ul>
        </div>
      </section>

      <section className="shell hx-feature hx-feature--rev">
        <div className="hx-feature__media">
          <img src="/website/img/24h-palliativ.jpg" alt="Betreuerin am Bett einer Seniorin" width={760} height={620} loading="lazy" />
        </div>
        <div className="hx-feature__body">
          <span className="hx-feature__num">04</span>
          <h2 className="h-block">
            <T k="betreuung24h.palliativ.titel" />
          </h2>
          <p className="hx-feature__lead">
            <T k="betreuung24h.palliativ.einleitung" />
          </p>
          <ul className="hx-checks">
            {points('betreuung24h.palliativ', 4).map((k) => (
              <li key={k}><Icon name="check" size={17} /><span><T k={k} /></span></li>
            ))}
          </ul>
        </div>
      </section>
      </div>

      {/* Verhinderungs- & Kurzzeitpflege */}
      <section className="shell section">
        <div className="hx-band">
          <div className="hx-band__lead">
            <h2 className="h-section">
              <T k="betreuung24h.verhinderung.titel" />
            </h2>
          </div>
          <ul className="hx-band__list">
            <li><T k="betreuung24h.verhinderung.absatz-1" /></li>
            <li><T k="betreuung24h.verhinderung.absatz-2" /></li>
          </ul>
        </div>
      </section>

      {/* Anstellung der Betreuungskraft und Einsatzgebiet nebeneinander */}
      <section className="shell section--tight hx-twocol">
        <div id="anstellung">
          <h2 className="h-block">
            <T k="betreuung24h.anstellung.titel" />
          </h2>
          <div className="prose">
            {[1, 2, 3].map((n) => (
              <p key={n}>
                <T k={`betreuung24h.anstellung.absatz-${n}`} />
              </p>
            ))}
          </div>
        </div>
        {/* Einsatzgebiet — die Orte, für die die Seite gefunden werden soll. Orte
            mit eigener Seite sind verlinkt (siehe orte.ts). */}
        <div>
          <h2 className="h-block">
            <T k="betreuung24h.einsatzgebiet.titel" />
          </h2>
          <p className="hx-muted-lead">
            <T k="betreuung24h.einsatzgebiet.text" />
          </p>
          <ul className="chip-list" aria-label="Orte im Einsatzgebiet">
            {EINSATZGEBIET_24H.map((name) => {
              const ort = ORTE.find((o) => o.name === name);
              return (
                <li key={name} className="chip">
                  {ort ? <Link to={ortLink(ort)}>{name}</Link> : name}
                </li>
              );
            })}
          </ul>
          <p className="note" style={{ marginTop: 14 }}>
            <T k="betreuung24h.einsatzgebiet.hinweis" />
          </p>
        </div>
      </section>

      <FaqSection titleKey="betreuung24h.faq.titel" numbers={FAQ_NUMBERS} keyOf={(n) => `betreuung24h.faq.${n}`} />

      <ContactSection topic="CARE_24H" />
    </>
  );
};

export default Betreuung24h;
